import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { createPortal } from 'react-dom'
import {
  arcD,
  iconInto,
  INNER_R,
  OUTER_R,
  polarSegments,
  SEG_GAP_DEG,
  WHEEL_SIZE,
} from './orbitGeometry'
import type { OrbitOpenState, OrbitSlice } from './orbitTypes'
import { useOrbitSkin } from './orbitSkinCore'
import './orbitStyles.css'

type OrbitProps = {
  state: OrbitOpenState
  onClose: () => void
  onSliceFire: (slice: OrbitSlice) => void
}

type OrbitPhase = 'opening' | 'ready' | 'selecting' | 'closed'

export const Orbit = ({ state, onClose, onSliceFire }: OrbitProps) => {
  const { skin } = useOrbitSkin()
  const [hovered, setHovered] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [pressed, setPressed] = useState<string | null>(null)
  const [closing, setClosing] = useState(false)

  const timersRef = useRef<number[]>([])
  const phaseRef = useRef<OrbitPhase>('opening')
  const sliceRefs = useRef<Array<SVGGElement | null>>([])

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id))
    timersRef.current = []
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (typeof window.requestAnimationFrame !== 'function') {
      const timeout = window.setTimeout(() => {
        setOpen(true)
        phaseRef.current = 'ready'
      }, 0)
      return () => window.clearTimeout(timeout)
    }
    const frame = window.requestAnimationFrame(() => {
      setOpen(true)
      phaseRef.current = 'ready'
    })
    return () => window.cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    return () => {
      clearTimers()
    }
  }, [clearTimers])

  const handleDismiss = useCallback(() => {
    // Dismissal before dispatch cancels any pending selection
    clearTimers()
    phaseRef.current = 'closed'
    setOpen(false)
    setClosing(true)
    onClose()
  }, [clearTimers, onClose])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        handleDismiss()
      }
    }
    const onWheel = () => handleDismiss()
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('wheel', onWheel, { capture: true, passive: true })
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('wheel', onWheel, { capture: true })
    }
  }, [handleDismiss])

  if (typeof document === 'undefined') return null

  // The cursor stays the wheel origin. orientArc() rotates its fan into the
  // largest safe viewport span; translating the wheel would break that math.
  const segments = polarSegments(state.slices.length, state.x, state.y)
  const label = state.slices.find((slice) => slice.id === hovered)?.label ?? state.centerLabel

  const fireSlice = (slice: OrbitSlice) => {
    if (slice.disabled || phaseRef.current === 'selecting' || phaseRef.current === 'closed') return

    if (slice.id === 'ctx-more' || slice.id === 'ctx-back') {
      onSliceFire(slice)
      return
    }

    phaseRef.current = 'selecting'
    setPressed(slice.id)

    const holdTimer = setTimeout(() => {
      setOpen(false)
      setClosing(true)
    }, 300)

    const dispatchTimer = setTimeout(() => {
      phaseRef.current = 'closed'
      onSliceFire(slice)
    }, 560)

    timersRef.current.push(holdTimer as unknown as number, dispatchTimer as unknown as number)
  }

  const handleSliceKeyDown = (event: ReactKeyboardEvent<SVGGElement>, index: number) => {
    const enabledIndices = state.slices
      .map((s, i) => (s.disabled ? -1 : i))
      .filter((i) => i !== -1)

    if (!enabledIndices.length) return

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      fireSlice(state.slices[index])
      return
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault()
      const currentPos = enabledIndices.indexOf(index)
      const nextIndex = enabledIndices[(currentPos + 1) % enabledIndices.length]
      sliceRefs.current[nextIndex]?.focus()
      return
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault()
      const currentPos = enabledIndices.indexOf(index)
      const prevIndex = enabledIndices[(currentPos - 1 + enabledIndices.length) % enabledIndices.length]
      sliceRefs.current[prevIndex]?.focus()
      return
    }

    if (event.key === 'Home') {
      event.preventDefault()
      sliceRefs.current[enabledIndices[0]]?.focus()
      return
    }

    if (event.key === 'End') {
      event.preventDefault()
      sliceRefs.current[enabledIndices[enabledIndices.length - 1]]?.focus()
      return
    }
  }

  return createPortal(
    <>
      <div
        className={`orbit-overlay ${open && !closing ? 'open' : ''}`}
        data-testid="orbit-overlay"
        onPointerDown={handleDismiss}
      />
      <div
        className={`orbit style-${skin} ${open ? 'open' : ''} ${closing ? 'closing' : ''}`}
        data-testid="orbit-root"
        role="menu"
        aria-label={state.centerLabel}
        style={{ left: state.x, top: state.y }}
      >
        <svg className="wheel" width={WHEEL_SIZE} height={WHEEL_SIZE} viewBox="0 0 204 204">
          {state.slices.map((slice, index) => {
            const segment = segments[index]
            const Icon = slice.icon
            const isPressed = pressed === slice.id
            const isDisabled = Boolean(slice.disabled)

            return (
              <g
                key={slice.id}
                ref={(el) => {
                  sliceRefs.current[index] = el
                }}
                className={`slice ${index < state.globalCount ? 'global' : 'ctx'} ${isPressed ? 'pressed' : ''} ${isDisabled ? 'disabled' : ''}`}
                style={{ '--i': index } as CSSProperties}
                role="menuitem"
                tabIndex={isDisabled ? -1 : 0}
                aria-label={slice.label}
                aria-disabled={isDisabled || undefined}
                onMouseEnter={() => {
                  if (!isDisabled) setHovered(slice.id)
                }}
                onMouseLeave={() => {
                  if (!isDisabled) setHovered(null)
                }}
                onFocus={() => {
                  if (!isDisabled) setHovered(slice.id)
                }}
                onBlur={() => {
                  if (!isDisabled) setHovered(null)
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  fireSlice(slice)
                }}
                onKeyDown={(e) => handleSliceKeyDown(e, index)}
              >
                {slice.disabledReason ? <title>{slice.disabledReason}</title> : null}
                <path
                  className="arc arc-wide"
                  d={arcD(segment.startDeg - SEG_GAP_DEG, segment.endDeg + SEG_GAP_DEG, INNER_R, OUTER_R)}
                />
                <path className="arc arc-normal" d={arcD(segment.startDeg, segment.endDeg, INNER_R, OUTER_R)} />
                <g
                  className="icon"
                  transform={iconInto(segment.iconX, segment.iconY)}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <Icon />
                </g>
                {isPressed ? <circle className="pulse" cx={segment.iconX} cy={segment.iconY} r={12} /> : null}
              </g>
            )
          })}
        </svg>
        <div className="center" aria-live="polite">
          <div>
            <div className={`label ${hovered ? 'is-hover' : ''}`}>{label}</div>
            <span className="sub">ORBIT</span>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
